import { ICellRendererParams } from 'ag-grid-community';
import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { InputChipsComponent } from '../../../chips/input-chips/input-chips.component';
import { AlertApiClientService } from '../../services/alert-api-client.service';

@Component({
  selector: 'app-bcc-renderer',
  templateUrl: './bcc-renderer.component.html',
  styleUrls: ['./bcc-renderer.component.scss'],
  standalone: false,
})
export class BccRendererComponent implements OnInit, OnDestroy {
  private static readonly VISIBLE_ROWS = 3;
  private static readonly LIST_HEIGHT = 140;

  private fb = inject(FormBuilder);
  private readonly alertApiClientService = inject(AlertApiClientService);
  private hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  @ViewChild('bccChips') private bccChips?: InputChipsComponent;

  get bccEmails(): string[] {
    return this.emails;
  }
  public bccForm!: FormGroup;
  public params!: ICellRendererParams;
  private emails: string[] = [];
  public separatorKeysCodes: number[] = [COMMA, ENTER, SPACE, SEMICOLON];
  public currentEditing: string[] = [];
  private lastSaved: string[] = [];

  public expanded = false;
  private justExpandedViaClick = false;
  private flushTimer: any;

  get maxVisibleRows(): number {
    return BccRendererComponent.VISIBLE_ROWS;
  }

  get maxListHeight(): number {
    return BccRendererComponent.LIST_HEIGHT;
  }
  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.emails = params.value || [];
    this.currentEditing.splice(
      0,
      this.currentEditing.length,
      ...(params.value || []),
    );
    this.lastSaved = [...this.currentEditing];
  }

  ngOnInit(): void {
    this.bccForm = new FormGroup({
      bccAlert: this.fb.control('', [Validators.email]),
    });
  }

  onChipsChange(updated: string[]): void {
    const normalised = updated.map((e) => e.trim()).filter(Boolean);
    this.currentEditing.splice(0, this.currentEditing.length, ...normalised);
    this.params.data.bcc = [...this.currentEditing];
    if (!this.expanded) {
      this.scheduleFlush();
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
    this.flushTimer = setTimeout(() => {
      this.flush();
    }, 250);
  }

  private flush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (!this.arrEqual(this.currentEditing, this.lastSaved)) {
      const payload = { ...this.params.data, bcc: this.currentEditing };
      this.alertApiClientService.updateAlert(payload).subscribe(() => {
        this.lastSaved = [...this.currentEditing];
        this.params.data.bcc = [...this.currentEditing];
      });
    }
  }

  private arrEqual(currentEditing: string[], lastSaved: string[]): boolean {
    if (currentEditing.length !== lastSaved.length) {
      return false;
    }
    return currentEditing.every((v, i) => v === lastSaved[i]);
  }

  onFocusOut(): void {
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (this.justExpandedViaClick) {
        return;
      }
      if (!active || !this.hostEl.nativeElement.contains(active)) {
        this.collapse();
      }
    }, 0);
  }

  collapse(): void {
    if (this.expanded) {
      this.flush();
      this.expanded = false;
    }
  }

  onExpandClick(event: MouseEvent): void {
    if (this.isRemoveIconClick(event)) {
      return;
    }
    event.stopPropagation();
    if (!this.expanded) {
      this.expand();
    }
  }

  private isRemoveIconClick(event: MouseEvent): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('.mat-mdc-chip-remove');
  }

  onMouseDown(event: MouseEvent): void {
    if (this.isRemoveIconClick(event)) {
      return;
    }
    if (!this.expanded) {
      this.justExpandedViaClick = true;
      this.expand();
    }
  }

  expand(): void {
    if (!this.expanded) {
      this.expanded = true;
      this.justExpandedViaClick = true;
      setTimeout(() => (this.justExpandedViaClick = false), 50);
      const chipsInstance: any = this.bccChips;
      if (chipsInstance && typeof chipsInstance.focusInput === 'function') {
        chipsInstance.focusInput();
      }
    }
  }

  ngOnDestroy(): void {
    this.flush();
  }
}
